/** SUNTRONIX ONE — Tenant portal: My Invoices. */
import { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ReceiptText, Download, Loader2 } from "lucide-react";

const money = (n, cur = "INR") => n == null ? "—" : `${cur === "INR" ? "₹" : cur + " "}${Number(n).toLocaleString("en-IN")}`;
const PAY = { PAID: "bg-emerald-50 text-emerald-700", PARTIAL: "bg-amber-50 text-amber-700", UNPAID: "bg-rose-50 text-rose-700" };

export default function MyInvoices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/tenant-catalogue/my-invoices"); setItems(data.items); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load invoices"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-5" data-testid="my-invoices-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Finance</div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><ReceiptText className="h-6 w-6 text-sky-600" />My Invoices</h1>
        <p className="text-sm text-slate-500">Tax invoices for your delivered orders.</p>
      </div>
      {loading ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div> : (
        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
          {items.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No invoices yet.</div>}
          {items.map((i) => (
            <div key={i.id} data-testid={`invoice-${i.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800">{i.invoice_no} <span className="text-[11px] text-slate-400">· {i.order_no}</span></div>
                <div className="text-[12px] text-slate-500">{i.invoice_date} · Taxable {money(i.taxable_value, i.currency)} · GST {money(i.gst, i.currency)} · <b>{money(i.total, i.currency)}</b></div>
              </div>
              <Badge className={PAY[i.payment_status] || "bg-slate-100 text-slate-600"}>{i.payment_status}</Badge>
              <Button size="sm" variant="outline" onClick={() => window.open(`${API}/tenant-catalogue/my-invoices/${i.id}/pdf`, "_blank")} data-testid={`invoice-pdf-${i.id}`}><Download className="h-3.5 w-3.5 mr-1" />PDF</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
